<template>
  <div class="q-pa-md">
    <div class="spam-filter-bar q-mb-md">
      <q-input
        v-model="nameInput"
        type="textarea"
        rows="2"
        label="Full names"
        placeholder="Paste names separated by commas or newlines. Include shows only matching records; Exclude hides them."
        class="q-mb-md"
      />
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <q-option-group
          v-model="mode"
          type="radio"
          inline
          color="primary"
          :options="modeOptions"
        />
        <div class="d-flex align-items-center">
          <q-btn
            class="mr-2"
            label="Apply"
            color="primary"
            @click="applyFilter"
          />
          <q-btn
            flat
            label="Clear"
            @click="clearFilter"
          />
        </div>
      </div>
    </div>

    <div class="spam-batch-bar q-mb-md">
      <div class="d-flex align-items-center flex-wrap">
        <q-checkbox
          class="mr-3"
          :value="selectableRows.length > 0 && selectableRows.every((row) => selectedIds.includes(row.id))"
          :disable="!selectableRows.length"
          label="Select this page"
          @input="togglePage"
        />
        <span class="mr-3 text-faded">{{ selectedIds.length }} selected across pages</span>
        <q-btn
          class="mr-2"
          label="Convert selected"
          color="primary"
          :disable="!selectedIds.length || converting"
          :loading="converting"
          @click="convert(selectedIds)"
        />
        <q-btn
          v-if="selectedIds.length"
          flat
          label="Clear selection"
          @click="selectedIds = []"
        />
      </div>
    </div>

    <div
      v-if="failures.length"
      class="text-negative q-mb-md"
    >
      <div
        v-for="failure in failures"
        :key="failure.id"
      >
        Spam #{{ failure.id }}: {{ failure.error }}
      </div>
    </div>

    <bj-data-table
      ref="dataTable"
      model="admin/spams"
      many-name="Spams"
      single-name="Spam"
      :columns="columns"
      :query-params="queryParams"
      :with-add-button="false"
      :with-cell-edit-button="false"
      :with-cell-delete-button="false"
      @fetched="onFetched"
    >
      <template
        slot="cell-select"
        slot-scope="{ row }"
      >
        <q-checkbox
          v-if="!row.converted_at"
          :value="selectedIds.includes(row.id)"
          @input="toggleSelected(row.id, $event)"
        />
      </template>
      <template
        slot="cell-created_at"
        slot-scope="{ row }"
      >
        {{ row.created_at }}
      </template>
      <template
        slot="cell-full_name"
        slot-scope="{ row }"
      >
        {{ row.full_name }}
      </template>
      <template
        slot="cell-email"
        slot-scope="{ row }"
      >
        {{ row.email }}
      </template>
      <template
        slot="cell-details"
        slot-scope="{ row }"
      >
        <div>Origin: {{ row.data.originId }}</div>
        <div>Property: {{ row.data.property_id }} · Unit: {{ row.data.unit_id || '—' }}</div>
        <div>{{ row.data.comments }}</div>
      </template>
      <template
        slot="cell-status"
        slot-scope="{ row }"
      >
        <router-link
          v-if="row.inquiry_id"
          :to="{ name: 'app.inquiries', params: { id: row.inquiry_id } }"
        >
          Inquiry #{{ row.inquiry_id }}
        </router-link>
        <span v-else>Pending</span>
      </template>
      <template
        slot="cell-actions"
        slot-scope="{ row }"
      >
        <q-btn
          v-if="!row.converted_at"
          size="sm"
          outline
          label="Convert"
          :disable="converting"
          @click="convert([row.id])"
        />
      </template>
    </bj-data-table>
  </div>
</template>

<script>
export default {
  data() {
    return {
      nameInput: '',
      mode: 'include',
      modeOptions: [
        { label: 'Include', value: 'include' },
        { label: 'Exclude', value: 'exclude' },
      ],
      queryParams: { full_names: [], mode: 'include' },
      selectedIds: [],
      pageRows: [],
      failures: [],
      converting: false,
      columns: [
        {
          name: 'select', field: 'id', label: '', sortable: false,
        },
        {
          name: 'id', field: 'id', label: 'ID', sortable: false,
        },
        {
          name: 'created_at', field: 'created_at', label: 'Received', sortable: false,
        },
        {
          name: 'full_name', field: 'full_name', label: 'Full name', sortable: false,
        },
        {
          name: 'email', field: 'email', label: 'Email', sortable: false,
        },
        {
          name: 'details', field: 'data', label: 'Inquiry details', sortable: false,
        },
        {
          name: 'status', field: 'converted_at', label: 'Status', sortable: false,
        },
      ],
    };
  },
  computed: {
    selectableRows() {
      return this.pageRows.filter((row) => !row.converted_at);
    },
  },
  methods: {
    applyFilter() {
      this.queryParams.full_names = [...new Set(this.nameInput.split(/[\n,]+/).map((name) => name.trim()).filter(Boolean))];
      this.queryParams.mode = this.mode;
      this.$refs.dataTable.refresh();
    },
    clearFilter() {
      this.nameInput = '';
      this.mode = 'include';
      this.applyFilter();
    },
    onFetched(queryParams, rows) {
      this.pageRows = rows;
    },
    toggleSelected(id, checked) {
      this.selectedIds = checked ? [...new Set([...this.selectedIds, id])] : this.selectedIds.filter((selected) => selected !== id);
    },
    togglePage(checked) {
      this.selectableRows.forEach((row) => this.toggleSelected(row.id, checked));
    },
    async convert(ids) {
      try {
        await this.uiConfirm(`Convert ${ids.length} saved spam record${ids.length === 1 ? '' : 's'} into inquiries?`);
      } catch (error) {
        return;
      }

      this.converting = true;
      this.failures = [];
      try {
        const response = await this.api().request('POST', 'admin/spams/convert', { ids });
        this.failures = response.results.filter((result) => result.error);
        const convertedIds = response.results.filter((result) => !result.error).map((result) => result.id);
        this.selectedIds = this.selectedIds.filter((id) => !convertedIds.includes(id));
        this.uiNotify(`${convertedIds.length} spam record${convertedIds.length === 1 ? '' : 's'} converted.`);
        this.$refs.dataTable.refresh();
      } catch (error) {
        this.uiNotify('Conversion request failed. Please retry.', 'negative');
      } finally {
        this.converting = false;
      }
    },
  },
};
</script>

<style scoped>
.spam-filter-bar,
.spam-batch-bar {
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #fafafa;
}
</style>
